import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';

const FunctionSelector = ({ agentId, nodeId, disabled = false }) => {
  const [loading, setLoading] = useState(true);
  const [functions, setFunctions] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [availableFunctions, setAvailableFunctions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchFunctions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/functions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFunctions(data);
      } else {
        console.error('Error fetching functions');
        showSnackbar('Error fetching functions', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error fetching functions', 'error');
    }
  };

  const fetchSelectedFunctions = async () => {
    try {
      // Determine the right endpoint based on whether we're working with an agent or a node
      const endpoint = nodeId 
        ? `${process.env.REACT_APP_API_URL}/api/workflow-node/${nodeId}/functions`
        : `${process.env.REACT_APP_API_URL}/api/agents/${agentId}/functions`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedFunctions(data);
      } else {
        console.error('Error fetching selected functions');
        showSnackbar('Error fetching selected functions', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error fetching selected functions', 'error');
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchFunctions();
      await fetchSelectedFunctions();
      setLoading(false);
    };
    
    loadData();
  }, [agentId, nodeId]);

  // Update available functions when either functions or selected functions change
  useEffect(() => {
    const selectedIds = selectedFunctions.map(f => f.id);
    setAvailableFunctions(functions.filter(f => !selectedIds.includes(f.id)));
  }, [functions, selectedFunctions]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const addFunction = async (functionId) => {
    try {
      // Determine the right endpoint based on whether we're working with an agent or a node
      const endpoint = nodeId
        ? `${process.env.REACT_APP_API_URL}/api/workflow-node/${nodeId}/functions/${functionId}`
        : `${process.env.REACT_APP_API_URL}/api/agents/${agentId}/functions/${functionId}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        await fetchSelectedFunctions();
        handleCloseDialog();
        showSnackbar('Function added successfully');
      } else {
        console.error('Error adding function');
        showSnackbar('Error adding function', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error adding function', 'error');
    }
  };

  const removeFunction = async (functionId) => {
    try {
      // Determine the right endpoint based on whether we're working with an agent or a node
      const endpoint = nodeId
        ? `${process.env.REACT_APP_API_URL}/api/workflow-node/${nodeId}/functions/${functionId}`
        : `${process.env.REACT_APP_API_URL}/api/agents/${agentId}/functions/${functionId}`;
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        }
      });
      
      if (response.ok) {
        await fetchSelectedFunctions();
        showSnackbar('Function removed successfully');
      } else {
        console.error('Error removing function');
        showSnackbar('Error removing function', 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      showSnackbar('Error removing function', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderFunctionTypeChip = (functionType) => {
    const typeMap = {
      'http_request': { label: 'HTTP', color: 'primary' },
      'webhook': { label: 'Webhook', color: 'secondary' },
      'news': { label: 'News', color: 'success' },
    };
    
    const config = typeMap[functionType] || { label: functionType, color: 'default' };
    
    return (
      <Chip 
        size="small" 
        label={config.label} 
        color={config.color} 
        sx={{ marginLeft: 1 }}
      />
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">
          Attached Functions
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          disabled={disabled || availableFunctions.length === 0}
        >
          Add Function
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : selectedFunctions.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <CodeIcon sx={{ color: 'text.secondary', fontSize: 40, mb: 1 }} />
          <Typography variant="body2" color="textSecondary">
            No functions attached
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add functions to enhance your agent's capabilities
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            disabled={disabled || availableFunctions.length === 0}
            sx={{ mt: 1 }}
          >
            Add Function
          </Button>
        </Paper>
      ) : (
        <List dense>
          {selectedFunctions.map((func) => (
            <ListItem key={func.id} divider>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {func.name}
                    {renderFunctionTypeChip(func.function_type)}
                  </Box>
                }
                secondary={func.description}
                secondaryTypographyProps={{ noWrap: true }}
              />
              <ListItemSecondaryAction>
                <IconButton 
                  edge="end" 
                  size="small"
                  onClick={() => removeFunction(func.id)}
                  disabled={disabled}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {/* Dialog for adding a function */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Function</DialogTitle>
        <DialogContent dividers>
          {availableFunctions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body1" color="textSecondary">
                No available functions to add
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                All functions are already attached or you need to create new functions
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                sx={{ mt: 1 }}
                onClick={() => window.location.href = '/functions'}
              >
                Create New Function
              </Button>
            </Box>
          ) : (
            <List>
              {availableFunctions.map((func) => (
                <ListItem 
                  key={func.id} 
                  button 
                  onClick={() => addFunction(func.id)}
                  divider
                >
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {func.name}
                        {renderFunctionTypeChip(func.function_type)}
                      </Box>
                    }
                    secondary={func.description}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FunctionSelector; 